import { supabase } from './supabase'
import { postFn } from './postFn'

// State-change activity types where a repeat within a short window is noise, not news:
// re-touching a show you're already "watching" (or a double-fire) must not insert a
// second feed row or fire a second push. 'episode' repeats are de-duped per episode.
const DEDUP_TYPES = new Set(['watching', 'rewatching', 'completed', 'added', 'dropped', 'rated', 'episode', 'watching_now'])
const DEDUP_WINDOW_MS = 12 * 3600 * 1000

// Write an activity entry - only if user has share_activity enabled
// `opts.skipBroadcast` (2026-09-14): used by a cascade mark's per-episode calls for
// the earlier episodes in the range — the row still gets written (feed, streak,
// stats all still see it), only the individual "X watched S·E" PUSH is suppressed,
// because the caller sends one consolidated push for the whole range separately.
// Without this, marking N episodes at once pushed every follower N times in a row
// (Jason, live: 6 pushes in one minute for one sitting of a show).
export async function writeActivity(userId, shareActivity, type, show, extra = {}, opts = {}) {
  if (!userId) return

  // De-dupe: skip the insert AND the push if the user already logged this same
  // (type, show[, episode]) recently. Fixes duplicate "started watching X" pushes
  // when a status is re-set or the caller double-fires (Jason 2026-07-11).
  if (show?.id && DEDUP_TYPES.has(type)) {
    try {
      const since = new Date(Date.now() - DEDUP_WINDOW_MS).toISOString()
      let q = supabase.from('activities').select('id')
        .eq('user_id', userId).eq('type', type).eq('show_id', show.id).gte('created_at', since)
      if (type === 'episode' || type === 'watching_now') {
        if (extra.season != null)  q = q.eq('data->>season', String(extra.season))
        if (extra.episode != null) q = q.eq('data->>episode', String(extra.episode))
      }
      const { data: dup } = await q.limit(1)
      if (dup && dup.length) return   // already logged recently, no duplicate row, no duplicate push
    } catch (_) { /* on query failure, fall through and write normally */ }
  }

  // Always write to own activity log (for digest), but mark as public/private
  await supabase.from('activities').insert({
    user_id:    userId,
    type,
    show_id:    show?.id   || null,
    show_name:  show?.name || null,
    show_poster:show?.poster_path || null,
    data: {
      ...extra,
      public: !!shareActivity,
    },
    created_at: new Date().toISOString(),
  }).then(({ error }) => {
    if (error) console.error('[activity write failed]', error.message, { type, show_name: show?.name })
  })

  // If user shares activity AND has followers who want notifications → trigger push
  if (shareActivity && !opts.skipBroadcast) {
    sendActivityNotifications(userId, type, show, extra).catch(() => {})
  }
}

async function sendActivityNotifications(userId, type, show, extra) {
  await postFn('/.netlify/functions/notify-activity', { type, show, extra }).catch(() => {})
}

export function activityLabel(type, showName, extra) {
  const name = showName || 'a show'
  switch(type) {
    case 'rated':      return `Rated ${name}, ${extra.label || `${extra.rating} reels`}`
    case 'completed':  return `Finished ${name}`
    case 'watching':   return `Started watching ${name}`
    case 'watching_now': return `Watching S${extra.season}·E${extra.episode} of ${name} right now`
    case 'rewatching': return `Rewatching ${name}`
    case 'binge':      return `Binged ${name}`
    case 'episode':    return `Watched S${extra.season}·E${extra.episode} of ${name}`
    case 'reviewed':   return `Reviewed ${name}`
    case 'added':      return `Added ${name} to list`
    case 'dropped':    return `Dropped ${name}`
    case 'reacted': {
      const where = extra.episode ? `S${extra.season}·E${extra.episode} of ${name}`
        : extra.season ? `the Season ${extra.season} finale of ${name}`
        : `the finale of ${name}`
      return `Reacted ${extra.emoji || ''} to ${where}`
    }
    case 'insync':     return `In sync with ${extra.with_name || extra.with_username || 'a friend'} on ${name} S${extra.season}·E${extra.episode}`
    default:           return `Updated ${name}`
  }
}
